import { deleteCookie, saveCookie } from '@strata-monorepo/utils/cookie'
import { LP_ANSWERS_ALLOWED_KEYS } from '../constants/lpAnswersAllowedKeys'

/** Cookie and localStorage key Monarch reads as `cookies.lpAnswers`. */
export const LP_ANSWERS_COOKIE = 'lpAnswers'

/**
 * Browser per-cookie limit covers the name, the value, and the attributes.
 * `lpAnswers=` plus `; path=/; SameSite=Lax` is the overhead saveCookie always adds.
 */
const COOKIE_ATTRIBUTE_BYTES = new TextEncoder().encode(
  `${encodeURIComponent(LP_ANSWERS_COOKIE)}=; path=/; SameSite=Lax`,
).length
const MAX_COOKIE_BYTES = 4096
const MAX_VALUE_BYTES = MAX_COOKIE_BYTES - COOKIE_ATTRIBUTE_BYTES

function isPairToken(value: string): boolean {
  if (value.includes('|') || value.includes(':')) return false

  for (const character of value) {
    const characterCode = character.charCodeAt(0)
    const isCookieOctet =
      characterCode === 0x21 ||
      (characterCode >= 0x23 && characterCode <= 0x2b) ||
      (characterCode >= 0x2d && characterCode <= 0x3a) ||
      (characterCode >= 0x3c && characterCode <= 0x5b) ||
      (characterCode >= 0x5d && characterCode <= 0x7e)
    if (!isCookieOctet) return false
  }

  return true
}

function scalarToString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value === '' ? null : value
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  return null
}

function arrayToString(values: unknown[]): string | null {
  const parts: string[] = []
  for (const element of values) {
    const rendered = scalarToString(element)
    if (rendered === null || !isPairToken(rendered)) continue
    parts.push(rendered)
  }
  return parts.length > 0 ? parts.join(',') : null
}

/**
 * Allow-listed quiz answers as `|key:value|key:value|`.
 * Returns null when nothing safe remains, including when no pair fits the cookie cap.
 */
export function buildLpAnswersValue(formResults: Record<string, unknown>): string | null {
  const pairs: string[] = []

  for (const [key, rawValue] of Object.entries(formResults)) {
    if (!LP_ANSWERS_ALLOWED_KEYS.has(key) || !isPairToken(key)) continue

    const pairValue = Array.isArray(rawValue) ? arrayToString(rawValue) : scalarToString(rawValue)
    if (pairValue === null) continue
    if (!Array.isArray(rawValue) && !isPairToken(pairValue)) continue

    pairs.push(`${key}:${pairValue}`)
  }

  while (pairs.length > 0) {
    const encoded = `|${pairs.join('|')}|`
    if (new TextEncoder().encode(encoded).length <= MAX_VALUE_BYTES) {
      return encoded
    }
    pairs.pop()
  }

  return null
}

function writeLocalStorage(value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(LP_ANSWERS_COOKIE)
      return
    }
    localStorage.setItem(LP_ANSWERS_COOKIE, value)
  } catch {
    // The cookie is what Monarch reads. Storage can be blocked without failing the quiz.
  }
}

/**
 * Writes the allow-listed quiz answers when the quiz finishes or the error
 * call-to-action runs. Only the keys in LP_ANSWERS_ALLOWED_KEYS are kept.
 * A null value clears the cookie and the matching localStorage key.
 */
export function persistLpAnswers(formResults: Record<string, unknown>): void {
  if (typeof document === 'undefined') return

  const value = buildLpAnswersValue(formResults)
  if (value === null) {
    deleteCookie(LP_ANSWERS_COOKIE)
    writeLocalStorage(null)
    return
  }

  saveCookie(LP_ANSWERS_COOKIE, value)
  writeLocalStorage(value)
}
